import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimActKc2PriceRowData, BimActKc2TotalItemData, BimPriceLevelEnum } from 'gql/graphql';
import { usePreviousValue } from 'hooks/usePrevious';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { actKc2TotalDetailsGql, bimActKC2PriceRowsGql } from '../bimWorkOrderActsKc2Queries';
import type { BimActKc2DataExtended } from '../types';
import { ActTotalDetailsType } from '../types';

const totalsRequestId = 'bimActsKc2TotalDetails';
const pricesRequestId = 'bimActKC2PriceRows';

export const useBimActsKc2DetailsData = (priceLevel: BimPriceLevelEnum | undefined, isReadOnly = false) => {
    const dispatch = useDispatch();

    const [checkedActForDetails, setCheckedActForDetails] = useState<BimActKc2DataExtended | null>(null);
    const [actDetailsType, setActDetailsType] = useState<ActTotalDetailsType>(ActTotalDetailsType.Totals);
    const [localPriceLevel, setLocalPriceLevel] = useState<BimPriceLevelEnum | undefined>(() => priceLevel);
    const [totalsData, setTotalsData] = useState<BimActKc2TotalItemData[] | null>();
    const [pricesData, setPricesData] = useState<BimActKc2PriceRowData[] | null>();

    const prevLocalPriceLevel = usePreviousValue(localPriceLevel);

    const [actKc2TotalDetailsData, actKc2TotalDetailsStatus, , actKc2TotalDetailsMakeRequest] = useRequest({
        requestId: totalsRequestId,
        query: actKc2TotalDetailsGql,
        withEffect: false,
    });

    const [bimActKC2PriceRowsData, bimActKC2PriceRowsStatus, , bimActKC2PriceRowsMakeRequest] = useRequest({
        requestId: pricesRequestId,
        query: bimActKC2PriceRowsGql,
        withEffect: false,
    });

    const [actKc2TotalDetailsSetData] = useResetStoreData({
        requestId: totalsRequestId,
    });

    const [bimActKC2PriceRowsSetData] = useResetStoreData({
        requestId: pricesRequestId,
    });

    const detailsTitle = useMemo(
        () => (checkedActForDetails ? `Акт КС-2 №${checkedActForDetails?.number}` : null),
        [checkedActForDetails],
    );

    const isDetailsOpened = !!checkedActForDetails;
    const isTotalsLoading = actKc2TotalDetailsStatus === RequestStatus.FETCHING;
    const isPricesLoading = bimActKC2PriceRowsStatus === RequestStatus.FETCHING;

    const closeDetails = useCallback(() => {
        setCheckedActForDetails(null);
        setTotalsData(undefined);
        setPricesData(undefined);
        setActDetailsType(ActTotalDetailsType.Totals);
        actKc2TotalDetailsSetData(undefined);
        bimActKC2PriceRowsSetData(undefined);
    }, [actKc2TotalDetailsSetData, bimActKC2PriceRowsSetData]);

    const refreshActsKc2TotalsTable = useCallback(() => {
        dispatch(actKc2TotalDetailsMakeRequest({ actKC2Id: checkedActForDetails?.id, readOnly: isReadOnly }));
    }, [dispatch, actKc2TotalDetailsMakeRequest, checkedActForDetails?.id, isReadOnly]);

    const refreshActsKc2PricesTable = useCallback(() => {
        if (localPriceLevel) {
            dispatch(
                bimActKC2PriceRowsMakeRequest({ actKC2Id: checkedActForDetails?.id, priceLevel: localPriceLevel }),
            );
        }
    }, [bimActKC2PriceRowsMakeRequest, checkedActForDetails?.id, dispatch, localPriceLevel]);

    useEffect(() => {
        if (isDetailsOpened && actDetailsType === ActTotalDetailsType.Totals && !totalsData) {
            refreshActsKc2TotalsTable();
        }
    }, [actDetailsType, isDetailsOpened, refreshActsKc2TotalsTable, totalsData]);

    useEffect(() => {
        if (
            isDetailsOpened &&
            actDetailsType === ActTotalDetailsType.Prices &&
            (!pricesData || localPriceLevel !== prevLocalPriceLevel)
        ) {
            refreshActsKc2PricesTable();
        }
    }, [
        actDetailsType,
        isDetailsOpened,
        pricesData,
        refreshActsKc2PricesTable,
        refreshActsKc2TotalsTable,
        totalsData,
        priceLevel,
        localPriceLevel,
        prevLocalPriceLevel,
    ]);

    useEffect(() => {
        if (actKc2TotalDetailsStatus === RequestStatus.SUCCESS) {
            setTotalsData(actKc2TotalDetailsData?.bimActKC2Totals?.items);
        }
    }, [actKc2TotalDetailsData?.bimActKC2Totals?.items, actKc2TotalDetailsStatus]);

    useEffect(() => {
        if (bimActKC2PriceRowsStatus === RequestStatus.SUCCESS) {
            setPricesData(bimActKC2PriceRowsData?.bimActKC2PriceRows?.items);
        }
    }, [bimActKC2PriceRowsData?.bimActKC2PriceRows?.items, bimActKC2PriceRowsStatus]);

    return {
        isDetailsOpened,
        detailsTitle,
        checkedActId: checkedActForDetails?.id,
        isTotalsLoading,
        isPricesLoading,
        setCheckedActForDetails,
        closeDetails,
        refreshActsKc2TotalsTable,
        refreshActsKc2PricesTable,
        actDetailsType,
        setActDetailsType,
        totalsData,
        pricesData,
        setLocalPriceLevel,
        localPriceLevel,
        hasDeflation: checkedActForDetails?.hasDeflation,
    };
};
